/**
 * Environment configuration for the application
 * This file contains the configuration for different environments
 */

export type Environment = 'local' | 'development' | 'test' | 'production';

interface EnvironmentConfig {
  apiUrl: string;
  mockApi: boolean;
  debug: boolean;
}

interface EnvironmentConfigs {
  [key: string]: EnvironmentConfig;
}

// Configuration for different environments
const environments: EnvironmentConfigs = {
  local: {
    apiUrl: 'http://localhost:9000/api',
    mockApi: false, // Connect to the real API running locally
    debug: true
  },
  development: {
    apiUrl: 'https://api.wine.skycaptech.dev/api',
    mockApi: false, // Connect to the dev API server
    debug: true
  },
  test: {
    apiUrl: 'https://test-api.notawine.com/api', // Test server
    mockApi: false,
    debug: true
  },
  production: {
    apiUrl: 'https://api.notawine.com/api', // Production server
    mockApi: false,
    debug: false
  }
};

// Get current environment from environment variable or default to local
export const getCurrentEnvironment = (): Environment => {
  const env = process.env.REACT_APP_ENV || 'local';
  return env as Environment;
};

// Get configuration for current environment
export const getConfig = (): EnvironmentConfig => {
  const env = getCurrentEnvironment();
  return environments[env] || environments.local;
};

// Export individual config values for convenience
export const config = {
  get apiUrl(): string {
    return getConfig().apiUrl;
  },
  
  get mockApi(): boolean {
    return getConfig().mockApi;
  },
  
  get debug(): boolean {
    return getConfig().debug;
  },
  
  // Helper to check current environment
  isProduction: (): boolean => {
    return getCurrentEnvironment() === 'production';
  },
  
  isDevelopment: (): boolean => {
    return getCurrentEnvironment() === 'development';
  },
  
  isTest: (): boolean => {
    return getCurrentEnvironment() === 'test';
  },
  
  isLocal: (): boolean => {
    return getCurrentEnvironment() === 'local';
  }
};

export default config;
